import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter";

const useStentMode = (
    sceneRef,
    cameraRef,
    modelRef,
    containerRef,
    controlsRef,
    viewportRef
) => {
    const [isStentMode, setIsStentMode] = useState(false);
    const [transformMode, setTransformMode] = useState("off"); // 'off' | 'translate' | 'rotate'

    // Stent Parameters
    const [upperDiameter, setUpperDiameter] = useState(20);
    const [innerDiameter, setInnerDiameter] = useState(15);
    const [lowerDiameter, setLowerDiameter] = useState(20);
    const [height, setHeight] = useState(30);

    const stentMeshRef = useRef(null);

    // Custom Interaction Refs
    const isDraggingRef = useRef(false);
    const previousMousePositionRef = useRef({ x: 0, y: 0 });
    const previousStentPositionRef = useRef(new THREE.Vector3());

    const generateHollowCylinderGeometry = useCallback((upDiam, inDiam, lowDiam, h) => {
        const points = [];
        const yHalf = h / 2;
        const rIn = inDiam / 2;
        const rTopOut = upDiam / 2;
        const rBotOut = lowDiam / 2;

        points.push(new THREE.Vector2(rIn, yHalf));
        points.push(new THREE.Vector2(rIn, -yHalf));
        points.push(new THREE.Vector2(rBotOut, -yHalf));
        points.push(new THREE.Vector2(rTopOut, yHalf));
        points.push(new THREE.Vector2(rIn, yHalf));

        const geometry = new THREE.LatheGeometry(points, 64, 0, Math.PI * 2);
        geometry.computeVertexNormals();

        return geometry;
    }, []);

    const clearStentFromScene = useCallback(() => {
        if (stentMeshRef.current && sceneRef.current) {
            sceneRef.current.remove(stentMeshRef.current);
            stentMeshRef.current.geometry.dispose();
            stentMeshRef.current.material.dispose();
            stentMeshRef.current = null;
        }
    }, [sceneRef]);

    const updateStentInScene = useCallback(() => {
        if (innerDiameter >= upperDiameter || innerDiameter >= lowerDiameter) {
            alert("İç çap, üst ve alt dış çaplardan kesinlikle küçük olmalıdır.");
            return;
        }

        if (!sceneRef.current) return;

        let existingPos = null;
        let existingQuat = null;

        if (stentMeshRef.current) {
            existingPos = stentMeshRef.current.position.clone();
            existingQuat = stentMeshRef.current.quaternion.clone();
            clearStentFromScene();
        }

        const geometry = generateHollowCylinderGeometry(
            upperDiameter,
            innerDiameter,
            lowerDiameter,
            height
        );

        const material = new THREE.MeshStandardMaterial({
            color: 0x888888,
            roughness: 0.4,
            metalness: 0.1,
            side: THREE.DoubleSide,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = "StentMesh";

        if (existingPos) {
            mesh.position.copy(existingPos);
            mesh.quaternion.copy(existingQuat);
        } else {
            if (modelRef.current && modelRef.current.children.length > 0) {
                // Place stent above the bounding box so it's clearly visible
                const box = new THREE.Box3().setFromObject(modelRef.current);
                const center = new THREE.Vector3();
                box.getCenter(center);
                mesh.position.set(center.x, box.max.y + 30, center.z);
            }
        }

        // Ensure it renders in both normal and depth-transparent passes
        mesh.layers.enable(0);
        mesh.layers.enable(1);

        sceneRef.current.add(mesh);
        stentMeshRef.current = mesh;

    }, [upperDiameter, innerDiameter, lowerDiameter, height, clearStentFromScene, generateHollowCylinderGeometry, sceneRef, modelRef]);

    const downloadStentSTL = useCallback(() => {
        if (!stentMeshRef.current) {
            alert("Lütfen önce stenti oluşturun.");
            return;
        }

        try {
            const exporter = new STLExporter();
            const stlString = exporter.parse(stentMeshRef.current, { binary: true });

            const blob = new Blob([stlString], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = url;
            link.download = `stent_${upperDiameter}_${innerDiameter}_${lowerDiameter}_${height}.stl`;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Error exporting STL", e);
            alert("STL dışa aktarma başarısız.");
        }
    }, [upperDiameter, innerDiameter, lowerDiameter, height]);

    // Handle disabling TrackballControls when transform is active
    useEffect(() => {
        if (controlsRef.current) {
            controlsRef.current.enabled = !(isStentMode && transformMode !== "off");
        }
    }, [isStentMode, transformMode, controlsRef]);

    // Handle custom Mouse Events for Stent translation and rotation
    useEffect(() => {
        if (!isStentMode || transformMode === "off" || !stentMeshRef.current || !cameraRef.current || !containerRef.current) {
            return;
        }

        const onMouseDown = (e) => {
            // Allow only left clicks and ignore UI clicks
            if (e.button !== 0 || e.target.closest(".ui-outside-scene")) return;

            isDraggingRef.current = true;
            previousMousePositionRef.current = { x: e.clientX, y: e.clientY };
        };

        const onMouseMove = (e) => {
            if (!isDraggingRef.current || !stentMeshRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const viewport = viewportRef?.current || {
                left: 0,
                top: 0,
                width: rect.width,
                height: rect.height,
            };

            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            if (transformMode === "translate") {
                const mouse = new THREE.Vector2(
                    ((mouseX - viewport.left) / viewport.width) * 2 - 1,
                    -((mouseY - viewport.top) / viewport.height) * 2 + 1
                );

                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(mouse, cameraRef.current);

                const visibleModels = modelRef.current ? modelRef.current.children.filter((child) => child.visible) : [];
                const intersects = raycaster.intersectObjects(visibleModels, true);

                if (intersects.length > 0) {
                    stentMeshRef.current.position.copy(intersects[0].point);
                } else {
                    const planeNormal = new THREE.Vector3();
                    cameraRef.current.getWorldDirection(planeNormal);
                    planeNormal.negate();

                    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, stentMeshRef.current.position);

                    const intersectionPoint = new THREE.Vector3();
                    raycaster.ray.intersectPlane(plane, intersectionPoint);

                    if (intersectionPoint) {
                        stentMeshRef.current.position.copy(intersectionPoint);
                    }
                }
            } else if (transformMode === "rotate") {
                const deltaX = e.clientX - previousMousePositionRef.current.x;
                const deltaY = e.clientY - previousMousePositionRef.current.y;

                const rotationSpeed = 0.01;

                // Horizontal drag -> rotate around world Y axis
                const worldUp = new THREE.Vector3(0, 1, 0);
                stentMeshRef.current.rotateOnWorldAxis(worldUp, deltaX * rotationSpeed);

                // Vertical drag -> rotate around camera's local X axis (right vector)
                const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(cameraRef.current.quaternion);
                stentMeshRef.current.rotateOnWorldAxis(camRight, deltaY * rotationSpeed);
            }

            previousMousePositionRef.current = { x: e.clientX, y: e.clientY };
        };

        const onMouseUp = (e) => {
            if (e.button !== 0) return;
            isDraggingRef.current = false;
        };

        const onWheel = (e) => {
            if (transformMode === "translate" && stentMeshRef.current) {
                // Prevent scrolling the page
                e.preventDefault();

                const depthSpeed = 0.5;
                // Move along the camera's negative Z axis (forward view)
                // e.deltaY is positive when scrolling down (pull towards us), negative when scrolling up (push deeper)
                // By multiplying cameraDirection, we move FORWARD when scrolling up (deltaY < 0).
                // Wait: cameraDirection points forward. If deltaY is negative, we want moving forward in camera space.
                const cameraDirection = new THREE.Vector3();
                cameraRef.current.getWorldDirection(cameraDirection);

                const translationStr = - Math.sign(e.deltaY) * depthSpeed;
                const offset = cameraDirection.clone().multiplyScalar(translationStr);

                stentMeshRef.current.position.add(offset);
            }
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("wheel", onWheel, { passive: false });

        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("wheel", onWheel);
            isDraggingRef.current = false;
        };
    }, [isStentMode, transformMode, cameraRef, containerRef, modelRef, viewportRef]);

    // Cleanup entirely when leaving mode
    useEffect(() => {
        if (!isStentMode) {
            clearStentFromScene();
            setTransformMode("off");
        }
    }, [isStentMode, clearStentFromScene]);

    return {
        isStentMode,
        setIsStentMode,
        transformMode,
        setTransformMode,
        upperDiameter,
        setUpperDiameter,
        innerDiameter,
        setInnerDiameter,
        lowerDiameter,
        setLowerDiameter,
        height,
        setHeight,
        updateStentInScene,
        downloadStentSTL,
    };
};

export default useStentMode;
